import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useEffect, useState } from "react";

import { api } from "../lib/api";

interface Settings {
  auto_pull: boolean;
  base_path: string;
  default_path_pattern: string;
  default_filename_pattern: string;
  working_window: string | null;
}

interface Preview {
  path: string;
  filename: string;
}

const SAMPLE = {
  client_name: "אבי כהן בע״מ",
  run_label: "ינואר 2026",
  document_label: "מע״מ",
  ext: "pdf",
  date: "2026-05-07",
};

const PATH_TOKENS = [
  { key: "client_name",    label: "שם לקוח", example: "אבי כהן בע״מ" },
  { key: "run_label",      label: "קמפיין",  example: "ינואר 2026" },
  { key: "document_label", label: "מסמך",    example: "מע״מ" },
  { key: "year",           label: "שנה",     example: "2026" },
  { key: "date",           label: "תאריך מלא", example: "2026-05-07" },
];

const FILE_TOKENS = [
  { key: "document_label", label: "מסמך",       example: "מע״מ" },
  { key: "year",           label: "שנה",         example: "2026" },
  { key: "date",           label: "תאריך מלא",  example: "2026-05-07" },
];

function parsePathKeys(pattern: string): string[] {
  return (pattern.match(/\{(\w+)\}/g) ?? []).map((m) => m.slice(1, -1));
}

function buildPathPattern(keys: string[]): string {
  return keys.length > 0 ? keys.map((k) => `{${k}}`).join("/") : "{client_name}";
}

function parseFilename(pattern: string): { keys: string[]; sep: string } {
  const noExt = pattern.replace(/\.?\{ext\}$/, "");
  const sep = noExt.includes("-") ? "-" : "_";
  const keys = (noExt.match(/\{(\w+)\}/g) ?? []).map((m) => m.slice(1, -1));
  return { keys, sep };
}

function buildFilenamePattern(keys: string[], sep: string): string {
  return (keys.length > 0 ? keys.map((k) => `{${k}}`).join(sep) : "{document_label}") + ".{ext}";
}

export function SettingsAgentScreen() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .agentSettings()
      .then(setSettings)
      .catch(() => setError("טעינת ההגדרות נכשלה."));
  }, []);

  useEffect(() => {
    if (!settings) return;
    let cancelled = false;
    api
      .previewPath({
        ...SAMPLE,
        path_pattern_override: settings.default_path_pattern,
        filename_pattern_override: settings.default_filename_pattern,
      })
      .then((p) => {
        if (!cancelled)
          setPreview({ path: p.resolved_path, filename: p.resolved_filename });
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => { cancelled = true; };
  }, [settings?.default_path_pattern, settings?.default_filename_pattern]);

  if (!settings) {
    return (
      <Group py="lg">
        {error ? <Text c="red">{error}</Text> : <Loader size="sm" />}
      </Group>
    );
  }

  const pathKeys = parsePathKeys(settings.default_path_pattern);
  const { keys: fileKeys, sep: fileSep } = parseFilename(settings.default_filename_pattern);

  const togglePathKey = (key: string) => {
    const next = pathKeys.includes(key)
      ? pathKeys.filter((k) => k !== key)
      : [...pathKeys, key];
    setSettings({ ...settings, default_path_pattern: buildPathPattern(next) });
  };

  const toggleFileKey = (key: string) => {
    const next = fileKeys.includes(key)
      ? fileKeys.filter((k) => k !== key)
      : [...fileKeys, key];
    setSettings({ ...settings, default_filename_pattern: buildFilenamePattern(next, fileSep) });
  };

  const setFileSep = (sep: string) => {
    setSettings({ ...settings, default_filename_pattern: buildFilenamePattern(fileKeys, sep) });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setSavedNote(null);
    try {
      await api.agentSettingsPatch({
        auto_pull: settings.auto_pull,
        base_path: settings.base_path,
        default_path_pattern: settings.default_path_pattern,
        default_filename_pattern: settings.default_filename_pattern,
      });
      setSavedNote("נשמר בהצלחה.");
    } catch {
      setError("שמירה נכשלה.");
    } finally {
      setBusy(false);
    }
  };

  const previewTree = preview ? (
    <Paper withBorder p="sm" style={{ fontFamily: "monospace", fontSize: 13 }}>
      <Stack gap={2}>
        {[settings.base_path.replace(/[\\/]+$/, ""), ...preview.path.split("/").filter(Boolean)].map((seg, i) => (
          <Group key={i} gap={4} wrap="nowrap" style={{ paddingRight: i * 16 }}>
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{i > 0 ? "└─" : ""}</Text>
            <Text size="sm">📁 {seg}{i === 0 ? "\\" : ""}</Text>
          </Group>
        ))}
        {(() => {
          const depth = [settings.base_path, ...preview.path.split("/").filter(Boolean)].length;
          return (
            <Group gap={4} wrap="nowrap" style={{ paddingRight: depth * 16 }}>
              <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>└─</Text>
              <Text size="sm" fw={500}>📄 {preview.filename}</Text>
            </Group>
          );
        })()}
      </Stack>
    </Paper>
  ) : <Loader size="sm" />;

  return (
    <Stack gap="md">
      <Title order={2}>הורדת קבצים מאושרים</Title>

      {/* ── 1. Auto-pull — full width ── */}
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1 }}>
            <Text fw={600}>הורדה אוטומטית לאחר אישור</Text>
            <Text size="sm" c="dimmed">
              {settings.auto_pull
                ? "קובץ יורד לתיקייה המוגדרת רק לאחר שסוקר אישר אותו בתיבת הקלט."
                : "קבצים לא יורדו אוטומטית. ניתן להוריד ידנית מתיבת הקלט לאחר האישור."}
            </Text>
          </Stack>
          <Switch
            size="md"
            checked={settings.auto_pull}
            onChange={(e) => setSettings({ ...settings, auto_pull: e.currentTarget.checked })}
          />
        </Group>
      </Paper>

      {/* ── 2. Path config + live preview side by side ── */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" style={{ alignItems: "start" }}>
        {/* Path controls */}
        <Stack gap="md">
          <Stack gap={4}>
            <Text fw={500}>תיקיית בסיס</Text>
            <Text size="xs" c="dimmed">הנתיב השורשי במחשב שלך שאליו יישמרו כל הקבצים.</Text>
            <TextInput
              value={settings.base_path}
              onChange={(e) => setSettings({ ...settings, base_path: e.currentTarget.value })}
              placeholder="C:\לקוחות"
              dir="ltr"
            />
          </Stack>

          <Stack gap={8}>
            <div>
              <Text fw={500}>מבנה תיקיית שמירה</Text>
              <Text size="xs" c="dimmed">לחץ על רכיב להוספה — הסדר נקבע לפי סדר הלחיצות. לחץ שוב להסרה.</Text>
            </div>
            {pathKeys.length > 0 && (
              <Group gap={4} align="center" wrap="wrap">
                {pathKeys.map((key, i) => {
                  const t = PATH_TOKENS.find((x) => x.key === key)!;
                  return (
                    <Group key={key} gap={4} align="center" wrap="nowrap">
                      {i > 0 && <Text c="dimmed" size="sm">\</Text>}
                      <Paper withBorder p="xs" style={{ background: "var(--mantine-color-blue-0)", borderColor: "var(--mantine-color-blue-5)", cursor: "pointer", textAlign: "center", minWidth: 80 }} onClick={() => togglePathKey(key)}>
                        <Text size="xs" fw={600} c="blue">{t.label} ✕</Text>
                        <Text size="xs" c="blue.7" style={{ opacity: 0.75 }}>{t.example}</Text>
                      </Paper>
                    </Group>
                  );
                })}
              </Group>
            )}
            <Group gap={6}>
              {PATH_TOKENS.filter((t) => !pathKeys.includes(t.key)).map((t) => (
                <Paper key={t.key} withBorder p="xs" style={{ cursor: "pointer", textAlign: "center", minWidth: 80 }} onClick={() => togglePathKey(t.key)}>
                  <Text size="xs" fw={600} c="dimmed">+ {t.label}</Text>
                  <Text size="xs" c="dimmed" style={{ opacity: 0.75 }}>{t.example}</Text>
                </Paper>
              ))}
            </Group>
            {pathKeys.length === 0 && <Text size="xs" c="red">יש לבחור לפחות רמה אחת</Text>}
          </Stack>
        </Stack>

        {/* Live preview */}
        <Stack gap={6}>
          <Text fw={500}>תצוגה מקדימה</Text>
          <Text size="xs" c="dimmed">הנתיב המלא על הדיסק:</Text>
          {previewTree}
        </Stack>
      </SimpleGrid>

      {/* ── 3. Filename builder — full width ── */}
      <Stack gap={8}>
        <div>
          <Text fw={500}>שם קובץ</Text>
          <Text size="xs" c="dimmed">לחץ על רכיב להוספה — הסדר נקבע לפי סדר הלחיצות. לחץ שוב להסרה.</Text>
        </div>
        <Group gap="xl" align="flex-start">
          <Stack gap={8} style={{ flex: 1 }}>
            {fileKeys.length > 0 && (
              <Group gap={4} align="center" wrap="wrap">
                {fileKeys.map((key, i) => {
                  const t = FILE_TOKENS.find((x) => x.key === key)!;
                  return (
                    <Group key={key} gap={4} align="center" wrap="nowrap">
                      {i > 0 && <Text c="dimmed" size="sm">{fileSep}</Text>}
                      <Paper withBorder p="xs" style={{ background: "var(--mantine-color-blue-0)", borderColor: "var(--mantine-color-blue-5)", cursor: "pointer", textAlign: "center", minWidth: 80 }} onClick={() => toggleFileKey(key)}>
                        <Text size="xs" fw={600} c="blue">{t.label} ✕</Text>
                        <Text size="xs" c="blue.7" style={{ opacity: 0.75 }}>{t.example}</Text>
                      </Paper>
                    </Group>
                  );
                })}
              </Group>
            )}
            <Group gap={6}>
              {FILE_TOKENS.filter((t) => !fileKeys.includes(t.key)).map((t) => (
                <Paper key={t.key} withBorder p="xs" style={{ cursor: "pointer", textAlign: "center", minWidth: 80 }} onClick={() => toggleFileKey(t.key)}>
                  <Text size="xs" fw={600} c="dimmed">+ {t.label}</Text>
                  <Text size="xs" c="dimmed" style={{ opacity: 0.75 }}>{t.example}</Text>
                </Paper>
              ))}
            </Group>
            <Group gap={8} align="center">
              <Text size="xs" c="dimmed">מפריד:</Text>
              <SegmentedControl
                size="xs"
                value={fileSep}
                onChange={setFileSep}
                data={[
                  { value: "_", label: "קו תחתון  ( _ )" },
                  { value: "-", label: "מקף  ( - )" },
                ]}
              />
            </Group>
          </Stack>
          <Stack gap={6} style={{ minWidth: 200 }}>
            <Text size="xs" c="dimmed">תצוגה מקדימה:</Text>
            <Group gap={4} align="center" wrap="nowrap" dir="ltr">
              {FILE_TOKENS.filter((t) => fileKeys.includes(t.key)).flatMap((t, i) => [
                i > 0 ? <Text key={`sep-${t.key}`} c="dimmed" size="sm">{fileSep}</Text> : null,
                <Badge key={t.key} variant="light" color="blue" size="lg" radius="sm">{t.example}</Badge>,
              ])}
              <Text c="dimmed" size="sm">.pdf</Text>
            </Group>
          </Stack>
        </Group>
      </Stack>

      {/* ── 4. Save ── */}
      <Group>
        <Button onClick={save} loading={busy} disabled={pathKeys.length === 0}>שמור</Button>
        {savedNote && <Text c="green" size="sm">{savedNote}</Text>}
        {error && <Text c="red" size="sm">{error}</Text>}
      </Group>
    </Stack>
  );
}
